/**
 * Teams Context Provider
 *
 * Manages Microsoft Teams SDK initialization, context, and theming.
 * Provides Teams-specific functionality to components throughout the application.
 */

import React, { createContext, useEffect, useState } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';

/**
 * Teams theme names as provided by the Teams SDK
 */
export type TeamsTheme = 'default' | 'dark' | 'contrast';

/**
 * Teams context information provided by the SDK
 */
export interface TeamsContextInfo {
  /**
   * The user's object ID in Azure AD
   */
  userObjectId?: string;

  /**
   * The user's principal name (UPN)
   */
  userPrincipalName?: string;

  /**
   * The tenant ID where the Teams app is installed
   */
  tenantId?: string;

  /**
   * The team ID (if running in a team context)
   */
  teamId?: string;

  /**
   * The channel ID (if running in a channel context)
   */
  channelId?: string;

  /**
   * The entity ID of the tab
   */
  entityId?: string;

  /**
   * The locale of the Teams client
   */
  locale?: string;
}

/**
 * Shape of the Teams context
 */
interface TeamsContextType {
  /**
   * Whether the app is running in Microsoft Teams
   */
  isInTeams: boolean;

  /**
   * Whether the Teams SDK has been initialized
   */
  isInitialized: boolean;

  /**
   * The current Teams theme
   */
  teamsTheme: TeamsTheme;

  /**
   * Teams context information
   */
  teamsContext: TeamsContextInfo | null;

  /**
   * Whether the SDK is currently initializing
   */
  isInitializing: boolean;

  /**
   * Any error that occurred during initialization
   */
  error: Error | null;

  /**
   * Gets an authentication token from Teams SSO
   * @returns Promise resolving to the auth token
   */
  getAuthToken: () => Promise<string>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const TeamsContext = createContext<TeamsContextType | undefined>(undefined);

export const TeamsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isInTeams, setIsInTeams] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [teamsTheme, setTeamsTheme] = useState<TeamsTheme>('default');
  const [teamsContext, setTeamsContext] = useState<TeamsContextInfo | null>(
    null
  );
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const initializeTeams = async () => {
      try {
        // Initialize the Teams SDK
        await microsoftTeams.app.initialize();

        setIsInTeams(true);
        setIsInitialized(true);

        // Get the Teams context
        const context = await microsoftTeams.app.getContext();

        // Extract relevant context information
        const contextInfo: TeamsContextInfo = {
          userObjectId: context.user?.id,
          userPrincipalName: context.user?.userPrincipalName,
          tenantId: context.user?.tenant?.id,
          teamId: context.team?.internalId,
          channelId: context.channel?.id,
          entityId: context.page?.id,
          locale: context.app.locale,
        };

        setTeamsContext(contextInfo);

        // Set initial theme
        const initialTheme = context.app.theme || 'default';
        setTeamsTheme(initialTheme as TeamsTheme);

        // Listen for theme changes
        microsoftTeams.app.registerOnThemeChangeHandler((theme: string) => {
          setTeamsTheme(theme as TeamsTheme);
        });

        // Notify Teams that the app has loaded successfully
        microsoftTeams.app.notifySuccess();
      } catch (err) {
        // If Teams SDK initialization fails, we're not in Teams
        console.warn('Teams SDK initialization failed - running outside Teams', err);
        setIsInTeams(false);
        setIsInitialized(false);
        setError(err as Error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeTeams();
  }, []);

  /**
   * Gets an authentication token using Teams SSO
   */
  const getAuthToken = async (): Promise<string> => {
    if (!isInTeams || !isInitialized) {
      throw new Error('Teams SDK not initialized');
    }

    try {
      // Request authentication token from Teams
      const token = await microsoftTeams.authentication.getAuthToken();
      return token;
    } catch (err) {
      console.error('Error getting Teams auth token:', err);
      throw err;
    }
  };

  const value: TeamsContextType = {
    isInTeams,
    isInitialized,
    teamsTheme,
    teamsContext,
    isInitializing,
    error,
    getAuthToken,
  };

  return (
    <TeamsContext.Provider value={value}>{children}</TeamsContext.Provider>
  );
};
